'use client'

import { usePathname } from 'next/navigation'
import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import debounce from 'util/debounce'

const SESSION_STORAGE_KEY = 'routeScrollPosition'

/**
 * Custom hook that tracks and saves the scroll position of a page in session storage.
 * @argument callback The current scroll position of the page.
 */
function useSessionScrollPositionEffect(
  callback?: (position: { x: number; y: number }) => void
): void {
  const pathname = usePathname()

  const [scrollPosition, setScrollPosition] = useState<{
    x: number
    y: number
  }>(() => {
    const storedScrollPosition = JSON.parse(
      (typeof sessionStorage !== 'undefined' &&
        sessionStorage.getItem(SESSION_STORAGE_KEY)) ||
        '{}'
    )
    const position: undefined | { x?: number; y?: number } =
      storedScrollPosition[pathname]

    return { x: position?.x || 0, y: position?.y || 0 }
  })

  // Save scroll position to session storage when navigating away from the page
  useEffect(() => {
    const handleScroll = debounce(200, () => {
      setScrollPosition({ x: window.scrollX, y: window.scrollY })
    })

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [pathname])

  useEffect(() => {
    // Save scroll position to session storage when navigating away from the page
    const handleBeforeUnload = () => {
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
    }
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  useEffect(() => {
    const handleUpdateSession = () => {
      const storedScrollPosition = JSON.parse(
        sessionStorage.getItem(SESSION_STORAGE_KEY) || '{}'
      )
      sessionStorage.setItem(
        SESSION_STORAGE_KEY,
        JSON.stringify({
          ...storedScrollPosition,
          [pathname]: scrollPosition
        })
      )
    }
    handleUpdateSession()
    if (callback) callback(scrollPosition)
  }, [pathname, scrollPosition, callback])

  useEffect(() => {
    window.scrollTo(scrollPosition.x, scrollPosition.y)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

export default useSessionScrollPositionEffect
